import {
  BarChart3,
  CreditCard,
  FileCode,
  FileText,
  Home,
  Mail,
  ScrollText,
  Settings,
  Upload,
  Users,
  type LucideIcon,
} from 'lucide-react';

export type NavItem = {
  title: string;
  url: string;
  icon: LucideIcon;
  roles: readonly string[];
  external?: boolean;
};

export type NavGroup = { title: string; items: NavItem[] };

const ALL = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'USER'] as const;
const STAFF = ['SUPER_ADMIN', 'ADMIN', 'MANAGER'] as const;
const ADMINS = ['SUPER_ADMIN', 'ADMIN'] as const;

// The one navigation list: the sidebar, Jump (⌘K), the phone header and the back link all read it.
export function navGroups(emailStudioUrl: string | null): NavGroup[] {
  return [
    {
      title: 'Work',
      items: [
        { title: 'Forms queue', url: '/dashboard/forms', icon: FileText, roles: ALL },
        { title: 'Today', url: '/dashboard', icon: Home, roles: ALL },
        { title: 'Emails', url: '/dashboard/emails', icon: Mail, roles: ALL },
      ],
    },
    {
      title: 'Records',
      items: [
        // USER sees Customers only in Records, and no money.
        { title: 'Customers', url: '/dashboard/customers', icon: Users, roles: ALL },
        { title: 'Payments', url: '/dashboard/payments', icon: CreditCard, roles: STAFF },
        { title: 'Analytics', url: '/dashboard/analytics', icon: BarChart3, roles: STAFF },
      ],
    },
    {
      title: 'Configure',
      items: [
        { title: 'Settings', url: '/dashboard/settings', icon: Settings, roles: ADMINS },
        { title: 'Logs', url: '/dashboard/logs', icon: ScrollText, roles: ADMINS },
        // Imports is a task you run, not a connection to configure, so it sits here for every staff role.
        { title: 'Imports', url: '/dashboard/imports', icon: Upload, roles: STAFF },
        // Managers cannot enter the admin-only Settings layout; admins reach Templates through System.
        { title: 'Templates', url: '/dashboard/templates', icon: FileCode, roles: ['MANAGER'] },
        ...(emailStudioUrl
          ? [{ title: 'Email Studio ↗', url: emailStudioUrl, icon: Mail, external: true, roles: STAFF }]
          : []),
      ],
    },
  ];
}

export function navForRole(role: string, emailStudioUrl: string | null): NavGroup[] {
  return navGroups(emailStudioUrl)
    .map((group) => ({ ...group, items: group.items.filter((item) => item.roles.includes(role)) }))
    .filter((group) => group.items.length > 0);
}

/** The item that owns a path: an exact match wins, otherwise the longest prefix (Today matches only /dashboard). */
export function navOwner(pathname: string, groups: NavGroup[] = navGroups(null)): NavItem | null {
  let best: NavItem | null = null;
  for (const item of groups.flatMap((group) => group.items)) {
    if (item.external) continue;
    const owns = pathname === item.url || (item.url !== '/dashboard' && pathname.startsWith(`${item.url}/`));
    if (owns && (!best || item.url.length > best.url.length)) best = item;
  }
  return best;
}

/** A top-level page is a nav destination itself; it is named by its title and the highlighted item, with no back link. */
export function isTopLevel(pathname: string): boolean {
  return navGroups(null).some((group) => group.items.some((item) => !item.external && item.url === pathname));
}
